import { fetchers } from "@/services/api/fetchers";
import { TodoInsert, TodoRow, TodoUpdate } from "@/services/api/fetchers/todos";
import { performOptimisticAction } from "@/shared/utils/store";
import { StateCreator } from "zustand";
import { BoundStore } from "./index";

export interface TodosSlice {
  todos: {
    items: TodoRow[];
    completed: TodoRow[];
    addTodo: (todo: TodoInsert) => Promise<TodoRow | undefined>;
    deleteTodo: (todoId: number) => Promise<void>;
    fetchTodos: () => Promise<TodoRow[]>;
    fetchCompletedTodos: () => Promise<TodoRow[]>;
    updateTodo: (todoId: number, updatedTodo: TodoUpdate) => Promise<TodoRow | undefined>;
  };
}

const createTodosSlice: StateCreator<BoundStore, [], [], TodosSlice> = (set, get) => ({
  todos: {
    items: [],
    completed: [],

    addTodo: async (todo) => {
      const existingTodos = get().todos.items;
      const existingCompleted = get().todos.completed;
      const tempId = Date.now();

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const newTodo = {
              ...todo,
              id: tempId,
              created_at: new Date().toISOString(),
              done: todo.done ?? false,
            } as TodoRow;
            const isCompleted = newTodo.done;
            return {
              todos: {
                ...state.todos,
                items: isCompleted ? state.todos.items : [...state.todos.items, newTodo],
                completed: isCompleted ? [...state.todos.completed, newTodo] : state.todos.completed,
              },
            };
          }),
        apiCall: async () => {
          const addedTodo = await fetchers.todos.addTodo(todo);
          set((state) => {
            const isCompleted = addedTodo.done;
            return {
              todos: {
                ...state.todos,
                items: isCompleted 
                  ? state.todos.items.filter((t) => t.id !== tempId)
                  : state.todos.items.map((t) => (t.id === tempId ? addedTodo : t)),
                completed: isCompleted
                  ? state.todos.completed
                      .map((t) => (t.id === tempId ? addedTodo : t))
                      .concat(!state.todos.completed.some((t) => t.id === addedTodo.id) ? [addedTodo] : [])
                  : state.todos.completed,
              },
            };
          });

          return addedTodo;
        },
        rollback: () =>
          set((state) => ({
            todos: {
              ...state.todos,
              items: existingTodos,
              completed: existingCompleted,
            },
          })),
      });
    },

    deleteTodo: async (todoId) => {
      const existingTodos = get().todos.items;
      const existingCompleted = get().todos.completed;

      const getDescendantIds = (items: TodoRow[], parentId: number): number[] => {
        const children = items.filter((item) => item.parent_id === parentId);
        let descendantIds: number[] = children.map((child) => child.id);
        for (const child of children) {
          descendantIds = descendantIds.concat(getDescendantIds(items, child.id));
        }
        return descendantIds;
      };

      const allTodos = [...existingTodos, ...existingCompleted];
      const idsToRemove = [todoId, ...getDescendantIds(allTodos, todoId)];

      await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => ({
            todos: {
              ...state.todos,
              items: state.todos.items.filter((todo) => !idsToRemove.includes(todo.id)),
              completed: state.todos.completed.filter((todo) => !idsToRemove.includes(todo.id)),
            },
          })),
        apiCall: async () => {
          // Deleting from children up to parent is safer with foreign key constraints.
          await Promise.all(idsToRemove.reverse().map((id) => fetchers.todos.deleteTodo(id)));
        },
        rollback: () =>
          set((state) => ({
            todos: {
              ...state.todos,
              items: existingTodos,
              completed: existingCompleted,
            },
          })),
      });
    },

    fetchTodos: async () => {
      try {
        const todos = await fetchers.todos.fetchTodos();
        set((state) => ({
          todos: {
            ...state.todos,
            items: todos,
          },
        }));
        return todos;
      } catch (error) {
        console.error("Failed to fetch todos:", error);
        return [];
      }
    },

    fetchCompletedTodos: async () => {
      try {
        const completed = await fetchers.todos.fetchCompletedTodos();
        set((state) => ({
          todos: {
            ...state.todos,
            completed,
          },
        }));
        return completed;
      } catch (error) {
        console.error("Failed to fetch completed todos:", error);
        return [];
      }
    },

    updateTodo: async (todoId, updatedTodo) => {
      const existingTodos = get().todos.items;
      const existingCompleted = get().todos.completed;
      const currentTodo = existingTodos.find((t) => t.id === todoId) || existingCompleted.find((t) => t.id === todoId);

      if (!currentTodo) return undefined;

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const updatedFullTodo = { ...currentTodo, ...updatedTodo } as TodoRow;
            const wasCompleted = currentTodo.done;
            const isNowCompleted = updatedFullTodo.done;
            const statusChanged = wasCompleted !== isNowCompleted;

            // Check if this is completed today
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);

            const isCompletedToday = updatedFullTodo.done_at
              ? new Date(updatedFullTodo.done_at) >= today && new Date(updatedFullTodo.done_at) < tomorrow
              : false;

            if (statusChanged) {
              // Moving between lists
              if (isNowCompleted) {
                // Keep in items if completed today, otherwise move to completed
                if (isCompletedToday) {
                  return {
                    todos: {
                      ...state.todos,
                      items: state.todos.items.map((t) => (t.id === todoId ? updatedFullTodo : t)),
                      completed: state.todos.completed,
                    },
                  };
                } else {
                  return {
                    todos: {
                      ...state.todos,
                      items: state.todos.items.filter((t) => t.id !== todoId),
                      completed: [...state.todos.completed, updatedFullTodo],
                    },
                  };
                }
              } else {
                // Uncompleting a todo - always move back to items
                return {
                  todos: {
                    ...state.todos,
                    items: [...state.todos.items.filter((t) => t.id !== todoId), updatedFullTodo],
                    completed: state.todos.completed.filter((t) => t.id !== todoId),
                  },
                };
              }
            } else {
              // Same list, just update
              return {
                todos: {
                  ...state.todos,
                  items: wasCompleted
                    ? state.todos.items
                    : state.todos.items.map((t) => (t.id === todoId ? updatedFullTodo : t)),
                  completed: wasCompleted
                    ? state.todos.completed.map((t) => (t.id === todoId ? updatedFullTodo : t))
                    : state.todos.completed,
                },
              };
            }
          }),
        apiCall: async () => fetchers.todos.updateTodo({ id: todoId, todo: updatedTodo }),
        rollback: () =>
          set((state) => ({
            todos: {
              ...state.todos,
              items: existingTodos,
              completed: existingCompleted,
            },
          })),
      });
    },
  },
});

export default createTodosSlice;
